<template>
    <div class="content-layout padded">
        <loading :loading="loading" :full-page="true"/>
        <div class="container cart">
            <client-only>
                <div v-if="items.length > 0">
                    <div class="row">
                        <div class="col-12">
                            <h1 class="page-heading">
                                Your cart ({{ items.length }} items) <span class="d-block d-sm-inline-block">All items listed in {{ localeVariables['currency'] }}</span>
                                <a style="padding: 5px 15px;" class="btn btn-default btn-hollow float-right" @click="clearCart">
                                    <b-icon-cart-x class="mr-2"/>
                                    Clear Cart</a>
                            </h1>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="cart-items-container container">
                                <div v-for="item in items" :key="item.key">
                                    <CartItem :item="item" @calculate-price="showPricePicker"/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="container">
                                <div>
                                    <div class="row cart-item-row">
                                        <div class="col-sm-12 col-md-7 col-lg-8 col-xl-9 license-agreement-col">
                                            <div>
                                                Read our Content Licence Agreement
                                                <a :href="$router.resolve({ name: 'content-licence-agreement' }).href" @click.prevent="$bvModal.show('content-licence-agreement-modal')">here</a>

                                                <div v-if="credit > 0" class="mt-4 btn-color remaining-credit">
                                                    You will have {{ moneyOptionalDecimal(user.credit_balance - credit) }} credit remaining after this purchase
                                                </div>

                                                <nuxt-link v-if="!hasDiscount" :to="{ name: 'pre-purchase', hash: '#prices-section' }" class="mt-4 d-block text-decoration-none buy-credits">
                                                    Would you like a <span class="btn-color">discount</span>? You could get {{ contentTextDescription }} for <strong>{{
                                                        moneyForceDecimal(subTotal - (subTotal * maxDiscountAvailable))
                                                    }}!</strong>
                                                    <br>
                                                    <span class="btn-color">Find out about <nobr>pre-purchasing</nobr> credits.</span> (Your images will stay in your cart).
                                                </nuxt-link>
                                            </div>
                                        </div>
                                        <div class="col-md-5 col-lg-4 col-xl-3">
                                            <table class="totals">
                                                <tbody>
                                                    <tr>
                                                        <td>Sub-Total</td>
                                                        <td class="highlighted amt-col">
                                                            {{ moneyForceDecimal(subTotal) }}
                                                        </td>
                                                    </tr>
                                                    <tr v-if="credit > 0">
                                                        <td>Credit</td>
                                                        <td class="highlighted amt-col">
                                                            -{{ moneyForceDecimal(credit) }}
                                                        </td>
                                                    </tr>
                                                    <tr v-if="gst > 0">
                                                        <td>Total GST ({{ localeVariables['gst-pcnt'] }}%)</td>
                                                        <td class="highlighted amt-col">
                                                            {{ moneyForceDecimal(gst) }}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>Total</td>
                                                        <td class="highlighted amt-col">
                                                            {{ moneyForceDecimal(total) }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="container">
                                <div>
                                    <div class="row cart-item-row no-border">
                                        <div class="col-md-7 col-lg-8 col-xl-9 checkout-btn-col">
                                            <div v-if="user && user.account_type === 'business'" class="alert alert-light text-center mb-0">
                                                <b-icon-lightning/>
                                                You can assign a Job or Purchase Order number to this transaction on the next page (optional).
                                            </div>
                                        </div>
                                        <div class="col-md-5 col-lg-4 col-xl-3">
                                            <nuxt-link :to="{name:'checkout'}" :class="{disabled:!readyToCheckOut}" class="btn btn-primary btn-large w-100">
                                                Checkout
                                            </nuxt-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="row">
                        <div class="col-12">
                            <h1 class="page-heading">
                                Your cart
                            </h1>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="">
                                You haven't added anything yet
                            </div>
                        </div>
                    </div>
                </div>
            </client-only>
        </div>
        <b-modal id="price-picker-modal" scrollable title="Available sizes" centered body-class="pb-0">
            <stock-price-picker ref="pricePicker" :image="pricePickerImage" :load-on-mounted="true" :picker-only="true" :preselect-default="false" @selected="readyToApplyNewSize = true"/>
            <template #modal-footer>
                <button class="btn btn-primary btn-medium" :disabled="!readyToApplyNewSize" @click="applyNewSize">Apply</button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Swal from 'sweetalert2'
import axios from 'axios'
import _ from 'lodash'
import Loading from '~/components/global/Loading.vue'
import CartItem from '~/components/CartItem'
import StockPricePicker from '~/components/stockImage/StockPricePicker.vue'

export default {
    components: {
        Loading,
        StockPricePicker,
        CartItem
    },
    data () {
        return {
            loading: true,
            pricePickerImage: null,
            readyToApplyNewSize: false
        }
    },
    head () {
        return {
            title: 'Cart - Truestock'
        }
    },
    computed: {
        ...mapGetters({
            items: 'cart/items',
            user: 'auth/user',
            localeVariables: 'data/localeVariables',
            maxPromotion: 'data/maxPromotion',
            creditPacks: 'data/creditPacks'
        }),
        hasDiscount () {
            return ((this.user && this.user.discount_pcnt > 0) || this.maxPromotion())
        },
        subTotal () {
            let subAmount = 0

            for (const item of this.items) {
                if (!item.size) {
                    continue
                }

                if (this.hasDiscount) {
                    let discountPcntAmt = 0

                    if (this.user && this.user.discount_pcnt > 0) {
                        discountPcntAmt = this.user.discount_pcnt
                    }

                    if (this.maxPromotion() && this.maxPromotion().discount_pcnt > discountPcntAmt) {
                        discountPcntAmt = this.maxPromotion().discount_pcnt
                    }

                    subAmount += parseFloat(item.size.price) - (parseFloat(item.size.price) * (discountPcntAmt / 100))
                } else {
                    subAmount += parseFloat(item.size.price)
                }
            }

            return subAmount
        },
        credit () {
            const creditBalance = this.user ? parseFloat(this.user.credit_balance) : 0
            return creditBalance >= this.subTotal ? this.subTotal : creditBalance
        },
        gst (fixed) {
            return (this.subTotal - this.credit) * (parseFloat(this.localeVariables['gst-pcnt']) / 100)
        },
        total () {
            return this.subTotal - this.credit + this.gst
        },
        readyToCheckOut () {
            return this.subTotal > 0 && this.items.filter(item => !item.size).length === 0
        },
        contentTextDescription () {
            if (this.items.length === 1) {
                return `this ${this.items[0].image.type}`
            }

            let text = ''

            const hasImage = this.items.some(item => item.image.type === 'image')
            const hasVideo = this.items.some(item => item.image.type === 'video')

            if (hasImage && hasVideo) {
                text = 'items'
            } else if (hasImage) {
                text = 'images'
            } else if (hasVideo) {
                text = 'videos'
            }

            return `these ${text}`
        },
        maxDiscountAvailable () {
            return (this.creditPacks.reduce((max, item) => Math.max(max, item.discount), 0)) / 100
        }
    },
    mounted () {
        const gaItems = []

        for (const item of this.items) {
            gaItems.push({
                item_id: item.image.code,
                item_name: item.image.description,
                quantity: 1
            })
        }

        this.$gtag.event('view_cart', {
            currency: this.localeVariables.currency,
            value: this.subTotal,
            items: gaItems
        })
    },
    async created () {
        if (this.user) {
            await this.$store.dispatch('auth/fetchUser')
        }

        await this.validateCart()
    },
    methods: {
        async validateCart () {
            this.loading = true

            await axios.post('/cart/validate', {
                cartItems: this.items
            })
                .then(rsp => rsp.data)
                .then((response) => {
                    if (response.length > 0) {
                        this.processCartValidationErrors(response)
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        processCartValidationErrors (errors) {
            errors.forEach((error) => {
                const item = this.items.find(item => item.image.code === error.itemCode)

                if (error.error === 'NotAvailable') {
                    this.$store.dispatch('cart/removeItem', { item })
                    this.$toast.error(`#${item.image.code} is no longer available and was removed`)
                } else if (error.error === 'InvalidPrice') {
                    const newItem = _.cloneDeep(item)
                    newItem.size.price = error.data.price

                    this.$store.dispatch('cart/updateInCart', { item: newItem })
                    this.$toast.info(`#${item.image.code} price has changed to ${this.moneyForceDecimal(newItem.size.price)}`)
                }
            })
        },
        applyNewSize () {
            const selectedSize = this.$refs.pricePicker.getSelected()

            this.$store.dispatch('cart/updateInCart', {
                item: {
                    image: selectedSize.image,
                    size: { ...selectedSize.size }
                }
            })

            this.$bvModal.hide('price-picker-modal')
        },
        showPricePicker (image) {
            this.readyToApplyNewSize = false

            this.pricePickerImage = image
            this.$bvModal.show('price-picker-modal')
        },
        clearCart () {
            Swal.fire({
                title: 'Clear Cart',
                icon: 'warning',
                html: 'Remove all items from your cart<br />and start again?',
                showDenyButton: false,
                showCancelButton: true,
                confirmButtonText: 'Yes'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$store.dispatch('cart/clearCart')
                }
            })
        },
        removeItem (item) {
            this.$gtag.event('remove_from_cart', {
                currency: this.localeVariables.currency,
                value: this.subTotal,
                items: [
                    {
                        item_id: item.image.code,
                        item_name: item.image.description,
                        quantity: 1
                    }
                ]
            })

            this.$store.dispatch('cart/removeItem', { item })
            this.$toast.success(`#${item.image.code} was removed`)
        }
    }
}
</script>
